<template>
  <div>
    <NuxtLayout name="default">
      <HeroStandardApollo
        :bg-image-source="$page?.faculty.heroImage!"
        :title="$page?.faculty?.title!"
        :full-width="false"
        class="bg-light-2"
        :display-text="true"
        title-size="large"
        title-variant="light-1"
        :display-gradient="true"
        :gradient-opacity="0.85"
        :show-text="true"
        :text="$page?.faculty?.heroSubtitle!"
        text-variant="secondary"
        text-size="xl"
        text-font-weight="bold"
      ></HeroStandardApollo>

      <!-- section study with world-class faculty - custom component -->
      <section
        class="py-space-lg pb-lg-0 pt-lg-space-xxl px-space-xs px-md-0 bg-light-2 text-white"
      >
        <div class="container px-space-sm px-md-0">
          <div class="row">
            <div
              class="col-12 offset-lg-1 col-lg-10 offset-xl-2 col-xl-8 d-flex flex-column flex-lg-row p-space-md ps-md-space-xl pt-md-space-xl"
              :style="fetchBgImageCSS($page?.faculty.introductionBgImage!)"
            >
              <div class="mb-space-sm mb-lg-0">
                <h2 class="h2-medium mb-space-sm">
                  {{ $page?.faculty.introductionTitle }}
                </h2>
                <div
                  class="fs-medium mb-space-sm"
                  v-html="$page?.faculty.introductionDescription"
                ></div>

                <div
                  class="fs-medium mb-space-sm text-secondary fw-bold"
                  v-html="$page?.faculty.introductionSecondaryText"
                ></div>

                <div
                  v-for="item in $page?.faculty.introductionStatistics"
                  :key="item?.id"
                  class="d-flex mb-space-sm flex-column flex-md-row"
                >
                  <div class="d-lg-flex align-items-center">
                    <h3
                      class="h3-xl bg-secondary py-space-xxs px-space-xs me-space-xs d-inline-block text-dark-3 mb-space-xs mb-lg-0"
                    >
                      {{ item?.title }}
                    </h3>
                    <p class="fw-bold fs-xl mb-0 wrap-text">
                      {{ item?.text }}
                    </p>
                  </div>
                </div>
              </div>
              <!-- image in large screen -->
              <div class="d-none d-lg-block ms-md-space-sm my-auto">
                <img
                  :alt="$page?.faculty.introductionImageAlt!"
                  class="world-class-faculty-img"
                  :src="$page?.faculty.introductionImage!"
                />
              </div>
              <!-- image in small screen -->
              <div class="row d-block d-lg-none">
                <div class="col-12">
                  <div class="text-center">
                    <img
                      :alt="$page?.faculty.introductionImageAlt!"
                      class="world-class-faculty-img"
                      :src="$page?.faculty.introductionImage!"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- section what sets ASU online faculty apart -->
      <section class="pb-space-lg py-lg-space-xxl bg-light-2">
        <div class="container">
          <div class="row">
            <div
              class="col-10 offset-1 col-lg-12 offset-lg-0 mb-space-sm mb-lg-space-lg"
            >
              <h2 class="h2-medium mb-space-sm text-center">
                {{ $page?.faculty.iconCardsTitle }}
              </h2>
              <p
                class="text-center fs-medium faculty-apart__text"
                v-html="$page?.faculty.iconCardsDescription"
              ></p>
            </div>
          </div>
          <div class="row row-cols-1 row-cols-md-2 row-cols-lg-3">
            <div
              v-for="card in $page?.faculty.iconCards"
              :key="card?.id"
              class="col mb-space-sm mb-md-space-md mb-lg-0"
            >
              <CardIcon
                :icon-source="card?.icon!"
                :icon-alt="card?.iconAlt!"
                :title="card?.title!"
                :display-cta="false"
                class="h-100"
              >
                <div v-html="card?.description"></div>
              </CardIcon>
            </div>
          </div>
        </div>
      </section>

      <!-- section meet our faculty -->

      <SectionTestimonialScout
        v-if="getFacultySlides"
        :title="$page?.faculty.meetFacultyTitle!"
        title-size="medium"
        :description="$page?.faculty.meetFacultyDescription!"
        :slides="getFacultySlides"
        @previousSlideClick="
          handleCarouselClick(
            $page?.faculty.meetFacultyTitle!,
            'carousel',
            'left chevron'
          )
        "
        @nextSlideClick="
          handleCarouselClick(
            $page?.faculty.meetFacultyTitle!,
            'carousel',
            'right chevron'
          )
        "
        @indicatorClick="
          handleCarouselClick(
            $page?.faculty.meetFacultyTitle!,
            'pagination',
            'owl dot'
          )
        "
      >
        <template v-slot="slotProps">
          <CardTestimonialApollo
            :slideIndex="slotProps.slideIndex"
            :slide="slotProps.slide"
            v-if="slotProps.slide"
            class="non-rds-link"
            @ctaClick="
              handleCarouselCtaClick(
                'open',
                slotProps.slide.title,
                $page?.faculty.meetFacultyTitle!,
                slotProps.slide.ctaText ? slotProps.slide.ctaText : ''
              )
            "
            @modalClose="
              handleModalClose(
                $page?.faculty.meetFacultyTitle!,
                slotProps.slide.title
              )
            "
          />
        </template>
      </SectionTestimonialScout>

      <!-- section ASU faculty accomplishments -->
      <SectionParallaxAtlas
        :bg-image-source="$page?.faculty.awardsImage!"
        container-bg-color="transparent"
        section-bg-color="light-2"
        :title="$page?.faculty.awardsTitle!"
      >
        <template #text>
          <div
            class="mb-space-md mb-lg-space-xl fs-medium"
            v-html="$page?.faculty.awardsDescription"
          ></div>
          <div class="row row-cols-2 row-cols-lg-3 mx-0">
            <div
              v-for="item in getCurrentAccomplishmentsItems"
              :key="item?.id"
              class="d-flex mb-space-md flex-column pe-space-xxs"
            >
              <div class="mb-space-xs">
                <h3
                  class="h3-xl bg-secondary py-space-xxs px-space-xs me-space-xs d-inline-block"
                >
                  {{ item?.title }}
                </h3>
              </div>
              <p class="fw-bold fs-large mb-space-sm wrap-text">
                {{ item?.text }}
              </p>
              <div class="d-inline-block">
                <NuxtLink
                  href="#"
                  class="non-rds-link"
                  @click.native="
                    setModalOpen(
                      item?.description!,
                      item?.text!,
                      $page?.faculty.awardsTitle!
                    )
                  "
                  >Learn more
                </NuxtLink>
              </div>
            </div>
            <!-- more items list -->

            <div
              v-for="item in getMoreAccomplishmentsItems"
              :key="item?.id"
              :class="[toggleShowAll ? 'fullHeight mb-space-md' : 'mb-0']"
              class="d-flex flex-column card-wrap-more pe-space-xxs"
            >
              <div class="mb-space-xs">
                <h3
                  class="h3-xl bg-secondary py-space-xxs px-space-xs me-space-xs d-inline-block"
                >
                  {{ item?.title }}
                </h3>
              </div>
              <p class="fw-bold fs-large mb-space-sm wrap-text">
                {{ item?.text }}
              </p>
              <div class="d-inline-block">
                <NuxtLink
                  class="non-rds-link"
                  @click.native="
                    setModalOpen(
                      item?.description!,
                      item?.text!,
                      $page?.faculty.awardsTitle!
                    )
                  "
                  >Learn more
                </NuxtLink>
              </div>
            </div>
          </div>
          <div class="d-flex justify-content-center">
            <NuxtLink
              @click.native="toggleDisplayShowAll"
              class="rds-link text-primary fs-large"
              >{{ getShowAllLessCaption }}
            </NuxtLink>
          </div>

          <RdsModal
            ref="awards-description-modal"
            use-custom-close
            :visible="showModal"
            @hidden="showModal = false"
            centered
          >
            <div class="d-block">
              <div
                class="d-flex justify-content-end align-items-center pe-space-xs pt-space-xs"
              >
                <button
                  type="button"
                  aria-label="Close"
                  class="modal-close ms-auto"
                  @click.native="setModalClose($page?.faculty.awardsTitle!)"
                >
                  ×
                </button>
              </div>
              <div
                v-html="awardDescription"
                class="px-space-md pb-space-md pt-space-lg"
              ></div>
            </div>
          </RdsModal>
        </template>
      </SectionParallaxAtlas>
      <!-- section hear from ASU professors about teaching online -->

      <section
        class="py-space-lg py-lg-space-xxl"
        v-if="$page?.faculty.facultyVideo"
      >
        <div class="container">
          <VideoModalAtlas
            :video-source="getVideoSource"
            video-position="right"
            :bg-image-source="$page.faculty.facultyVideo.imageSource!"
            :display-title="true"
            :title="$page.faculty.facultyVideo.title!"
            title-size="medium"
            @modal-open="handleVideoModalOpen"
            @modal-close="handleVideoModalClose"
          >
            <div
              class="fs-medium"
              v-html="$page.faculty.facultyVideo.description"
            ></div>
          </VideoModalAtlas>
        </div>
      </section>

      <!-- section solving tomorrow's problem today -->
      <section class="py-space-lg py-lg-space-xxl bg-light-2">
        <div class="container pb-lg-space-xxxl mb-lg-space-md">
          <div class="row">
            <div class="col-10 col-lg-8">
              <h2 class="h2-medium mb-space-sm">
                {{ $page?.faculty.researchTitle }}
              </h2>
            </div>
            <div class="col-12 col-lg-8">
              <div class="mb-space-md mb-lg-space-sm">
                <div v-html="$page?.faculty.researchDescription!"></div>
              </div>
            </div>
          </div>
          <div class="row row-cols-1 row-cols-md-2 row-cols-lg-3">
            <div
              v-for="card in $page?.faculty.researchCards"
              :key="card?.id"
              class="col mb-space-sm mb-lg-0"
            >
              <CardInfoVertical
                :image-source="card?.image!"
                :image-alt="card?.imageAlt!"
                :title="card?.title!"
                :display-title-highlight="false"
                :cta-as-button="true"
                :cta-button-block="false"
                :cta-text="card?.link?.text!"
                cta-text-size="small"
                cta-text-variant="dark-3"
                cta-text-weight="bold"
                :cta-link="card?.link?.uri!"
                cta-link-target="_blank"
                @civCtaClick="
                  handleCardCtaClick(
                    $event,
                    $page?.faculty.researchTitle!,
                    card!,
                    '_blank'
                  )
                "
                class="h-100"
              >
                <div v-html="card?.text" class="mb-space-sm"></div>
              </CardInfoVertical>
            </div>
          </div>
        </div>
      </section>

      <!-- section faculty jobs -->
      <SectionParallaxApollo
        :bg-image-source="$page?.faculty.jobsImage!"
        :title="$page?.faculty.jobsTitle!"
        title-level="h3"
        title-size="medium"
        :highlight-width="8"
        :cta-as-button="true"
        cta-button-variant="secondary"
        :cta-text="$page?.faculty?.jobsLink?.text!"
        cta-text-size="small"
        cta-text-variant="dark-3"
        cta-text-weight="bold"
        backdrop-height="296"
        :cta-link="$page?.faculty?.jobsLink?.uri!"
        class="py-space-lg py-lg-0"
        @ctaClick="
          handleParallaxLinkClick(
            $event,
            $page?.faculty.jobsTitle!,
            $page?.faculty.jobsLink
          )
        "
      >
        <div v-html="$page?.faculty.jobsDescription" class="mb-space-sm"></div>
      </SectionParallaxApollo>

      <!-- section faculty news and stories -->
      <section
        class="faculty-news section-bg rds-lazy"
        :data-background-image="`${$page?.faculty.newsBgImage}`"
      >
        <div class="container py-space-lg py-lg-space-xxl">
          <div class="row">
            <div class="col-12">
              <div class="mb-space-md mb-lg-space-sm">
                <h2 class="h2-medium">
                  {{ $page?.faculty.newsTitle }}
                </h2>
              </div>
            </div>
          </div>
          <div class="row row-cols-1 row-cols-md-2 row-cols-lg-3">
            <div
              v-for="(card, index) in $page?.faculty.newsArticles"
              :key="`news-articles-${index}`"
              class="col mb-space-sm mb-lg-0"
            >
              <CardImageArticle
                border-variant="light-4"
                :clickable-card="true"
                :image-source="card?.coverImage!"
                :image-alt="card?.coverImageAlt!"
                :title="card?.title!"
                :date="card?.updatedDate ? formatDate(card?.updatedDate) : ''"
                :minutes="card?.timeToRead!"
                :cta-link="card?.detailPage!"
                cta-text="Learn more"
                class="h-100"
                @cardClick="
                  handleCardCtaClick(
                    $event,
                    $page?.faculty.newsTitle!,
                    card!,
                    '_self'
                  )
                "
                @ctaClick="
                  handleCardCtaClick(
                    $event,
                    $page?.faculty.newsTitle!,
                    card!,
                    '_self'
                  )
                "
                @cardEnter="
                  handleCardCtaClick(
                    $event,
                    $page?.faculty.newsTitle!,
                    card!,
                    '_self'
                  )
                "
              ></CardImageArticle>
            </div>
          </div>
        </div>
      </section>

      <!-- section-rfi-multi-step -->
      <SectionRfiMultiStep :bg-image="RfiBgImage"></SectionRfiMultiStep>
    </NuxtLayout>
  </div>
</template>

<script setup lang="ts">
import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";
import { CardIcon } from "@rds-vue-ui/card-icon";
import { CardImageArticle } from "@rds-vue-ui/card-image-article";
import { CardInfoVertical } from "@rds-vue-ui/card-info-vertical";
import {
CardTestimonialApollo,
Slide,
} from "@rds-vue-ui/card-testimonial-apollo";
import HeroStandardApollo from "@rds-vue-ui/hero-standard-apollo/HeroStandardApollo.vue";
import { RdsModal } from "@rds-vue-ui/rds-modal";
import { SectionParallaxApollo } from "@rds-vue-ui/section-parallax-apollo";
import { SectionTestimonialScout } from "@rds-vue-ui/section-testimonial-scout";
import { VideoModalAtlas } from "@rds-vue-ui/video-modal-atlas";
import RfiBgImage from "~/assets/img/rfi-bg-overlay.png";
import SectionRfiMultiStep from "~/components/rfi/SectionRfiMultiStep.vue";
import { useUtilityService } from "~/composables/useUtilityService";
import {
Faculty,
Maybe,
NewsArticle,
ParagraphCardImage,
ParagraphHtmlLink,
} from "~/server/resolvers/resolvers-types";

definePageMeta({
  layout: false,
  __dangerouslyDisableSanitizersByTagID: {
    "ldjson-schema": ["innerHTML"],
  },
});

const config = useRuntimeConfig();

const UtilityService = useUtilityService();
const toggleShowAll = ref(false);
const initialItemsNumber = ref(6);
const awardText = ref("");
const awardDescription = ref("");
const showModal = ref<boolean>(false);

interface FacultyResponse {
  faculty: Faculty;
}

const $page = await usePageQuery<FacultyResponse>(
  `#graphql
      query faculty($facultyId: ID!){
        faculty(id:$facultyId) {
          id
          detailPage
          metaTitle
          metaDescription
          heroImage
          title
          heroSubtitle
          introductionBgImage
          introductionTitle
          introductionDescription
          introductionSecondaryText
          introductionStatistics {
            id
            title
            text
          }
          introductionImage
          introductionImageAlt
          iconCardsTitle
          iconCardsDescription
          iconCards {
            id
            title
            description
            icon
            iconAlt
          }
          awardsTitle
          awardsDescription
          awardsImage
          awardsImageAlt
          awardsBgImage
          awardsStats {
            id
            title
            text
            description
          }
          facultyVideo {
            id
            title
            description
            youtubeId
            imageSource
            imageAlt
            uploadDate
            duration
            captionText
          }
          meetFacultyTitle
          meetFacultyDescription
          meetFacultyCards {
            id
            name
            quote
            longDescription
            ctaText
            videoId
            designation
            collegeName
            image
            imageAlt
          }
          researchTitle
          researchDescription
          researchCards {
            id
            title
            image
            imageAlt
            text
            link {
              id
              text
              uri
              target
            }
          }
          jobsTitle
          jobsDescription
          jobsImage
          jobsLink {
            text
            uri
            target
          }
          newsTitle
          newsBgImage
          newsArticles {
            coverImage
            coverImageAlt
            title
            timeToRead
            updatedDate
            detailPage
            publishedDate
          }
        }
      }`,
  {
    facultyId: "52c6015b-c24c-42d3-9589-1a5077e07e2d",
  },
  { key: "faculty-page" }
);

useHead({
  title: $page?.faculty.metaTitle,
  meta: [
    { name: "Title", content: $page?.faculty.metaTitle },
    {
      name: "Description",
      content: $page?.faculty.metaDescription,
    },
  ],
  link: [
    {
      rel: "canonical",
      href: UtilityService.formatCanonicalUrl(
        `${config.public.siteUrl}${$page?.faculty.detailPage}`
      ),
    },
  ],
  script: [useSchemaService().generateAboutUsFacultySchema($page?.faculty)],
});

const fetchBgImageCSS = (img: string) => {
  return { "background-image": `url('${img}')` };
};

const getShowAllLessCaption = computed(() => {
  return toggleShowAll.value ? "Show less" : "Show all";
});

const toggleDisplayShowAll = () => {
  let actionValue;
  if (toggleShowAll.value) {
    actionValue = "close";
  } else {
    actionValue = "open";
  }
  analyticsComposable.trackCollapseEvent(
    "onclick",
    actionValue,
    "click",
    "main content",
    $page?.faculty?.awardsTitle?.toLowerCase()!,
    getShowAllLessCaption?.value?.toLowerCase()!,
    "parallax - container"
  );
  toggleShowAll.value = !toggleShowAll.value;
};

const handleCarouselCtaClick = (
  action: string,
  name: string,
  component: string,
  ctaText: string
) => {
  let textValue;

  if (action === "open") {
    textValue = ctaText.toLowerCase();
  } else {
    textValue = "close cross";
  }

  analyticsComposable.trackModalEvent(
    "onclick",
    action,
    "click",
    "main content",
    name.toLowerCase(),
    textValue,
    component.toLowerCase()
  );
};

const handleModalClose = (component: string, section: string) => {
  analyticsComposable.trackModalEvent(
    "onclick",
    "close",
    "click",
    "main content",
    section.toLowerCase(),
    "close cross",
    component.toLowerCase()
  );
};
const handleCardCtaClick = (
  event: Event,
  section: string,
  card: NewsArticle | ParagraphCardImage,
  target: string
) => {
  // Click handler for card-info-vertical
  event.preventDefault();

  if (card.title && "detailPage" in card && target === "_self") {
    analyticsComposable.trackLinkEvent(
      "internal link",
      "main content",
      card.title.toLowerCase(),
      "learn more",
      section.toLowerCase()
    );

    navigateTo(card.detailPage, {
      open: {
        target: "_self",
      },
    });
  } else if (
    card.title &&
    "link" in card &&
    typeof card.link?.text === "string"
  ) {
    analyticsComposable.trackLinkEvent(
      "internal link",
      "main content",
      card.title.toLowerCase(),
      card.link!.text.toLowerCase(),
      section.toLowerCase()
    );
    navigateTo(card.link.uri, {
      open: {
        target: "_blank",
      },
    });
  } else {
    console.error("cta missing link");
  }
};

const formatDate = (date: string) => {
  return UtilityService.formatDate(date);
};

const handleCarouselClick = (title: string, type: string, text: string) => {
  analyticsComposable.trackSelectEvent(
    "onclick",
    "click",
    type,
    "main content",
    title.toLowerCase(),
    text
  );
};

const handleParallaxLinkClick = (
  e: Event,
  section: string,
  link: Maybe<ParagraphHtmlLink> | undefined
) => {
  e.preventDefault();

  if (
    link &&
    typeof link !== "undefined" &&
    link.text &&
    link.uri &&
    link.target
  ) {
    analyticsComposable.trackLinkEvent(
      "internal link",
      "main content",
      section.toLowerCase(),
      link.text.toLowerCase()
    );
    navigateTo(link.uri, {
      open: {
        target: link.target,
      },
    });
  } else {
    console.error("link missing text or href");
  }
};

const getVideoSource = computed(() => {
  return `https://www.youtube.com/embed/${$page?.faculty?.facultyVideo?.youtubeId}`;
});

const getCurrentAccomplishmentsItems = computed(() => {
  const items = $page?.faculty.awardsStats;
  return items?.slice(0, initialItemsNumber.value);
});

const getMoreAccomplishmentsItems = computed(() => {
  const items = $page?.faculty?.awardsStats;
  return items?.slice(initialItemsNumber.value, items?.length);
});

const setModalOpen = (content: string, section: string, component: string) => {
  showModal.value = true;
  awardText.value = section;
  awardDescription.value = content;
  analyticsComposable.trackModalEvent(
    "onclick",
    "open",
    "click",
    "main content",
    section.toLowerCase(),
    "learn more",
    component.toLowerCase()
  );
};

const setModalClose = (component: string) => {
  showModal.value = false;
  analyticsComposable.trackModalEvent(
    "onclick",
    "close",
    "click",
    "main content",
    awardText.value.toLowerCase(),
    "close cross",
    component.toLowerCase()
  );
};

const handleVideoModalOpen = () => {
  analyticsComposable.trackModalEvent(
    "onclick",
    "open",
    "click",
    "main content",
    $page?.faculty?.facultyVideo?.title!.toLowerCase()!,
    "play button"
  );
};

const handleVideoModalClose = () => {
  analyticsComposable.trackModalEvent(
    "onclick",
    "close",
    "click",
    "main content",
    $page?.faculty?.facultyVideo?.title!.toLowerCase()!,
    "close cross"
  );
};

const getFacultySlides = computed<Slide[] | undefined>(() => {
  const slides = $page?.faculty?.meetFacultyCards?.map((card) => {
    return {
      id: card?.id,
      title: card?.name,
      ctaText: card?.ctaText,
      labelA: card?.designation,
      labelB: card?.collegeName,
      bodyText: card?.quote,
      modalText: card?.longDescription,
      src: card?.image,
      imageAlt: card?.imageAlt,
      videoId: card?.videoId,
    } as Slide;
  });

  return slides;
});
</script>

<style lang="scss" scoped>
.world-class-faculty-img {
  height: 280px;
}

.faculty-apart__text {
  margin: 0 auto;
}

:deep(.bg-backdrop) {
  margin-top: 0px !important;
}

:deep(.card-icon-height) {
  height: 53px;
}

:deep(.content-container) {
  .content-div {
    background-image: url("https://live-asuocms.ws.asu.edu/sites/default/files/2022-02/topo-pattern.jpg");
  }
}

.card-wrap-more {
  max-height: 0;
  height: auto;
  overflow: hidden;
  opacity: 0;
  transition: all 0.4s ease-out;
}

.fullHeight {
  max-height: 350px;
  opacity: 1;
  transition: all 0.4s ease-in;
}

.faculty-news {
  margin: 0;
}

.section-bg {
  position: relative;
  background-size: auto;
  background-position: top;
}

:deep(main#content) {
  background-color: var(--rds-light-2);
}

@media (min-width: 992px) {
  .world-class-faculty-img {
    height: 475px;
    margin-right: -7rem;
  }

  .faculty-apart__text {
    max-width: 675px;
  }
}
</style>
